import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
const Footer = () => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [hoveredLinks, setHoveredLinks] = useState({});
  const [hoveredSocial, setHoveredSocial] = useState(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine grid layout based on screen size
  const getGridTemplateColumns = () => {
    if (windowWidth < 640) {
      return '1fr'; // Single column on mobile
    } else if (windowWidth < 1024) {
      return '1fr 1fr'; // Two columns on tablet
    } else {
      return '2fr repeat(3, 1fr)'; // Original layout on desktop
    }
  };

  // Colors based on PlasticAnalytics branding
  const colors = {
    blue: 'rgb(26, 42, 108)',
    orange: 'rgb(230, 126, 34)',
    darkText: '#111827',
    lightText: '#6B7280',
    border: '#E5E7EB',
    hover: 'rgb(26, 42, 108)',
    hoverBg: '#F3F8FF'
  };

  const styles = {
    footer: {
      backgroundColor: 'white',
      borderTop: `1px solid ${colors.border}`,
      padding: windowWidth < 640 ? '32px 0 20px' : '48px 0 24px',
      marginTop: 'auto'
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 20px'
    },
    content: {
      display: 'grid',
      gridTemplateColumns: getGridTemplateColumns(),
      gap: windowWidth < 640 ? '32px' : windowWidth < 1024 ? '40px' : '48px',
      marginBottom: windowWidth < 640 ? '32px' : '48px'
    },
    brandingContainer: {
      gridColumn: windowWidth < 1024 ? '1 / -1' : 'auto', // Full width on tablet and mobile
      marginBottom: windowWidth < 1024 ? '8px' : '0'
    },
    branding: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: windowWidth < 1024 ? '100%' : '300px'
    },
    logo: {
      fontSize: '24px',
      fontWeight: '600',
      color: colors.blue,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    logoIcon: {
      height: '28px',
      borderRadius: '6px'
    },
    tagline: {
      fontSize: '14px',
      color: colors.lightText,
      lineHeight: '1.5',
      maxWidth: windowWidth < 1024 ? '450px' : '300px'
    },
    socialLinks: {
      display: 'flex',
      gap: '12px',
      marginTop: '8px'
    },
    socialIcon: (isHovered) => ({
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isHovered ? colors.hoverBg : '#F3F4F6',
      color: isHovered ? colors.blue : colors.lightText,
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }),
    columnContainer: {
      display: 'flex',
      flexDirection: windowWidth < 640 ? 'column' : 'row',
      gap: windowWidth < 640 ? '32px' : 'inherit',
      gridColumn: windowWidth < 1024 && windowWidth >= 640 ? '1 / -1' : 'auto'
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: windowWidth < 1024 && windowWidth >= 640 ? '1' : 'auto'
    },
    columnTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: colors.darkText,
      marginBottom: '8px'
    },
    link: (key, isHovered) => ({
      fontSize: '14px',
      color: isHovered ? colors.blue : colors.lightText,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      cursor: 'pointer',
      padding: '4px 0',
      position: 'relative',
      display: 'inline-block',
      // Show underline animation on hover
      '&::after': {
        content: '""',
        position: 'absolute',
        width: isHovered ? '100%' : '0',
        height: '1px',
        bottom: '0',
        left: '0',
        backgroundColor: colors.blue,
        transition: 'width 0.3s ease'
      }
    }),
    bottom: {
      borderTop: `1px solid ${colors.border}`,
      paddingTop: windowWidth < 768 ? '20px' : '24px',
      display: 'flex',
      flexDirection: windowWidth < 768 ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: windowWidth < 768 ? 'flex-start' : 'center',
      gap: windowWidth < 768 ? '16px' : '0'
    },
    copyright: {
      fontSize: '14px',
      color: colors.lightText
    },
    bottomLinks: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: windowWidth < 480 ? '12px 24px' : '24px',
      marginTop: windowWidth < 768 ? '8px' : '0'
    }
  };

  // Links data
  const footerLinks = {
    solutions: [
      { label: 'Analysis Platform', url: '/analysis' },
      { label: 'Spectral Database', url: '/database' },
      { label: 'Research Tools', url: '/research' }
    ],
    company: [
      { label: 'About Us', url: '/about' },
      { label: 'Careers', url: '/careers' },
      { label: 'Contact', url: '/contact' }
    ],
    resources: [
      { label: 'Documentation', url: '/documentation' },
      { label: 'API Reference', url: '/api' },
      { label: 'Support', url: '/support' }
    ]
  };

  // Handle hover states for links
  const handleLinkHover = (section, index, isHovered) => {
    setHoveredLinks(prev => ({
      ...prev,
      [`${section}-${index}`]: isHovered
    }));
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.brandingContainer}>
            <div style={styles.branding}>
              <div style={styles.logo}>
                <img src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Flogo.png?alt=media&token=e2c15ffa-4af0-4f55-af64-545ae75fe511" style={{
                  ...styles.logoIcon,
                  backgroundColor: colors.blue
                }} />
                Plastic Analytics
              </div>
              <p style={styles.tagline}>
                Transforming plastic analysis through innovative spectral technology and data solutions.
              </p>
              <div style={styles.socialLinks}>
                <a 
                  href="https://twitter.com/plasticanalytic" 
                  style={styles.socialIcon(hoveredSocial === 'twitter')}
                  onMouseEnter={() => setHoveredSocial('twitter')}
                  onMouseLeave={() => setHoveredSocial(null)}
                  aria-label="Twitter"
                >
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/plasticanalytics" 
                  style={styles.socialIcon(hoveredSocial === 'linkedin')}
                  onMouseEnter={() => setHoveredSocial('linkedin')}
                  onMouseLeave={() => setHoveredSocial(null)}
                  aria-label="LinkedIn"
                >
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {windowWidth < 1024 && windowWidth >= 640 ? (
            <div style={styles.columnContainer}>
              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Solutions</h3>
                {footerLinks.solutions.map((link, index) => (
                  <Link 
                    key={`solutions-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`solutions-${index}`, hoveredLinks[`solutions-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`solutions-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('solutions', index, true)}
                    onMouseLeave={() => handleLinkHover('solutions', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              
              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Company</h3>
                {footerLinks.company.map((link, index) => (
                  <Link 
                    key={`company-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`company-${index}`, hoveredLinks[`company-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`company-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('company', index, true)}
                    onMouseLeave={() => handleLinkHover('company', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              
              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Resources</h3>
                {footerLinks.resources.map((link, index) => (
                  <Link 
                    key={`resources-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`resources-${index}`, hoveredLinks[`resources-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`resources-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('resources', index, true)}
                    onMouseLeave={() => handleLinkHover('resources', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            // Standard three-column layout for desktop or single column for mobile
            <>
              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Solutions</h3>
                {footerLinks.solutions.map((link, index) => (
                  <Link 
                    key={`solutions-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`solutions-${index}`, hoveredLinks[`solutions-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`solutions-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('solutions', index, true)}
                    onMouseLeave={() => handleLinkHover('solutions', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Company</h3>
                {footerLinks.company.map((link, index) => (
                  <Link 
                    key={`company-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`company-${index}`, hoveredLinks[`company-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`company-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('company', index, true)}
                    onMouseLeave={() => handleLinkHover('company', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <div style={styles.column}>
                <h3 style={styles.columnTitle}>Resources</h3>
                {footerLinks.resources.map((link, index) => (
                  <Link 
                    key={`resources-${index}`}
                    to={link.url} 
                    style={{
                      ...styles.link(`resources-${index}`, hoveredLinks[`resources-${index}`]),
                      '::after': {
                        content: '""',
                        position: 'absolute',
                        width: hoveredLinks[`resources-${index}`] ? '100%' : '0%',
                        height: '1px',
                        bottom: '0',
                        left: '0',
                        backgroundColor: colors.blue,
                        transition: 'width 0.3s ease'
                      }
                    }}
                    onMouseEnter={() => handleLinkHover('resources', index, true)}
                    onMouseLeave={() => handleLinkHover('resources', index, false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={styles.bottom}>
          <div style={styles.copyright}>
            © {new Date().getFullYear()} Plastic Analytics. All rights reserved.
          </div>
          <div style={styles.bottomLinks}>
            <Link 
              to="/privacy" 
              style={{
                ...styles.link('privacy', hoveredLinks['privacy']),
                '::after': {
                  content: '""',
                  position: 'absolute',
                  width: hoveredLinks['privacy'] ? '100%' : '0%',
                  height: '1px',
                  bottom: '0',
                  left: '0',
                  backgroundColor: colors.blue,
                  transition: 'width 0.3s ease'
                }
              }}
              onMouseEnter={() => handleLinkHover('privacy', 0, true)}
              onMouseLeave={() => handleLinkHover('privacy', 0, false)}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              style={{
                ...styles.link('terms', hoveredLinks['terms']),
                '::after': {
                  content: '""',
                  position: 'absolute',
                  width: hoveredLinks['terms'] ? '100%' : '0%',
                  height: '1px',
                  bottom: '0',
                  left: '0',
                  backgroundColor: colors.blue,
                  transition: 'width 0.3s ease'
                }
              }}
              onMouseEnter={() => handleLinkHover('terms', 0, true)}
              onMouseLeave={() => handleLinkHover('terms', 0, false)}
            >
              Terms of Service
            </Link>
            <Link 
              to="/cookies" 
              style={{
                ...styles.link('cookies', hoveredLinks['cookies']),
                '::after': {
                  content: '""',
                  position: 'absolute',
                  width: hoveredLinks['cookies'] ? '100%' : '0%',
                  height: '1px',
                  bottom: '0',
                  left: '0',
                  backgroundColor: colors.blue,
                  transition: 'width 0.3s ease'
                }
              }}
              onMouseEnter={() => handleLinkHover('cookies', 0, true)}
              onMouseLeave={() => handleLinkHover('cookies', 0, false)}
            >
              Cookie Settings
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;